import {
  MusicNoteIdentity,
  MusicNoteName,
  MusicNoteSimpleWeight,
  MusicNoteWithinChord,
  MusicNoteWithinScale,
  NoteAccidental,
} from "../../wasm/pkg/wasm";
import { ChordInScaleEdit, findNoteByHalfsteps, listAllNoteIds } from "../models/wasm.model";
import { AudioHistoryState, buildUniqueScaleFamilyId, ChordHistoryPerScale } from "../statemachines/AudioHistoryState";
import { chooseSharpsFlatsGivenMinorRoot } from "./chooseSharpsFlatsGivenMinorRoot";

/*
 * input:   type MusicNoteIdentity = "A" | "AsharpBflat" | "B"
 * output:  type MusicNoteName = "A" | "Asharp" | "Aflat" | ...
 *
 * Caution1: ignoring all the scales with doublesharps or flats
 * Caution2: relies on the calling code to correctly choose 'Sharp' or 'Flat' or 'Natural' based on the root note.
 */
function toMusicNoteNameGivenScaleFamilyContext(
  id: MusicNoteIdentity,
  scaleSharpFlat: NoteAccidental,
  priorNoteName?: MusicNoteName
): MusicNoteName {
  if (id.length === 1) {
    if (id === "B" && priorNoteName === "Bflat") {
      return "Cflat";
    } else if (id === "E" && priorNoteName === "Eflat") {
      return "Fflat";
    } else if (id === "C" && priorNoteName === "Asharp") {
      return "Bsharp";
    } else if (id === "F" && priorNoteName === "Dsharp") {
      return "Esharp";
    }
    return id as MusicNoteName; // this is wrong.  F is sometimes E#, E is sometimes Fb, C is sometimes B#, B is sometimes Cb
  }
  let [sharpName, flatName] = id.split("sharp"); // relying on id names, ex: AsharpBflat
  if (scaleSharpFlat === "Flat") {
    return flatName as MusicNoteName; // NoteName ex: Bflat
  } else {
    return `${sharpName}sharp` as MusicNoteName; // NoteName ex: Asharp
  }
}

type ScaleTranscriptionMaps = {
  noteIdTranslationMap: Partial<Record<MusicNoteIdentity, MusicNoteIdentity>>;
  noteNameMap: Partial<Record<MusicNoteIdentity, MusicNoteName>>;
};
function mapToNewNoteWithinChord<T extends MusicNoteWithinChord | MusicNoteWithinScale>(old: T, maps: ScaleTranscriptionMaps): T {
  const note_id = maps.noteIdTranslationMap[old.note_id]!; // Assert all possible notes are available from scale family
  return {
    ...old,
    note_id,
    note_name: maps.noteNameMap[note_id]!,
  };
}
function transcribeScaleAndChords(
  chordsPerScale: ChordHistoryPerScale,
  /** A map from old note ID to new note ID */
  noteIdTranslationMap: Partial<Record<MusicNoteIdentity, MusicNoteIdentity>>,
  userSharpFlat: NoteAccidental
): ChordHistoryPerScale {
  const newMinorRootId = noteIdTranslationMap[chordsPerScale.scale.family.minor_tonic_id]!;
  const newSharpFlat = chooseSharpsFlatsGivenMinorRoot(newMinorRootId, userSharpFlat);
  // track the previous note name to avoid output with both B and B# (for example)
  let priorNoteName: MusicNoteName | undefined = undefined;
  /** A map from new note ID to new note Name.  To be used for every chord in history */
  let noteNameMap: Partial<Record<MusicNoteIdentity, MusicNoteName>> = {}; // NOT translated, using new info
  // Replacements.  But calculated during the pre-work iteration rather than in a separate map func.
  let newScaleFamilyNotes: Array<MusicNoteWithinScale> = [];

  chordsPerScale.scale.family.notes.forEach((n) => {
    const new_note_id = noteIdTranslationMap[n.note_id]!;
    const new_note_name = toMusicNoteNameGivenScaleFamilyContext(new_note_id, newSharpFlat, priorNoteName);
    priorNoteName = new_note_name;
    noteIdTranslationMap[n.note_id] = new_note_id;
    noteNameMap[new_note_id] = new_note_name;
    newScaleFamilyNotes.push({
      ...n,
      note_id: new_note_id,
      note_name: new_note_name,
    });
  });

  // Can't re-use the below closure because this is MusicNoteWithinScale[], not MusicNoteWithinChord.
  const newShapeNotes = chordsPerScale.scale.shape_notes.map((sn) => {
    const note_id = noteIdTranslationMap[sn.note_id]!; // Assert all possible notes are available from scale family
    return {
      ...sn,
      note_id,
      note_name: noteNameMap[note_id]!,
    };
  });

  const maps = {
    noteIdTranslationMap,
    noteNameMap,
  };

  const newChordHistory: ChordInScaleEdit[][] = chordsPerScale.chordHistory.map((cArr) => {
    return cArr.map((chord) => {
      return {
        ...chord,
        bass_note_id: maps.noteIdTranslationMap[chord.bass_note_id]!,
        root_note: mapToNewNoteWithinChord(chord.root_note, maps),
        notes: chord.notes.map((n) => mapToNewNoteWithinChord(n, maps)),
        silent_notes: chord.silent_notes.map((n) => mapToNewNoteWithinChord(n, maps)),
      };
    });
  });

  const result: ChordHistoryPerScale = {
    ...chordsPerScale,
    chordHistory: newChordHistory,
    scale: {
      ...chordsPerScale.scale,
      family: {
        ...chordsPerScale.scale.family,
        minor_tonic_id: newMinorRootId,
        notes: newScaleFamilyNotes,
        scale_uses_sharps_or_flats: newSharpFlat,
      },
      shape_notes: newShapeNotes,
    },
  };

  return result;
}

/** rotate the notes - just once */
function buildNoteIdTranslationMap(howManyHalfSteps: number): Record<MusicNoteIdentity, MusicNoteIdentity> {
  let noteIdTranslationMap: Partial<Record<MusicNoteIdentity, MusicNoteIdentity>> = {};
  listAllNoteIds().forEach((oldId) => {
    noteIdTranslationMap[oldId] = findNoteByHalfsteps(oldId, howManyHalfSteps);
  });
  return noteIdTranslationMap as Record<MusicNoteIdentity, MusicNoteIdentity>;
}

export function transcribeAudioHistory(
  audioHistory: AudioHistoryState,
  howManyHalfSteps: number,
  userSharpFlat: NoteAccidental
): AudioHistoryState {
  const noteIdTranslationMap = buildNoteIdTranslationMap(howManyHalfSteps);
  const oldScaleIds = Object.keys(audioHistory.chordsInScales);
  let newChordsInScales: { [scaleId: string]: ChordHistoryPerScale } = {};
  let scaleIdMap: { [oldId: string]: string } = {};
  oldScaleIds.forEach((oldScaleId) => {
    const newChordsPerScale = transcribeScaleAndChords(audioHistory.chordsInScales[oldScaleId], noteIdTranslationMap, userSharpFlat);
    const newScaleId = buildUniqueScaleFamilyId(newChordsPerScale.scale);
    scaleIdMap[oldScaleId] = newScaleId;
    newChordsInScales[newScaleId] = newChordsPerScale;
  });

  const newPrimaryScaleId = scaleIdMap[audioHistory.primaryScaleId!];
  const newPrimaryScale = newChordsInScales[newPrimaryScaleId].scale;
  const newNoteHistory = audioHistory.noteHistory.map((nArr) => {
    return nArr.map((old) => {
      return {
        ...old,
        // ignoring potential primary octave rotation, don't really care.
        identity: noteIdTranslationMap[old.identity],
      };
    });
  });

  let newTotalNoteWeights: { [noteId: string]: MusicNoteSimpleWeight } = {};
  Object.entries(noteIdTranslationMap).forEach(([oldId, newId]) => {
    newTotalNoteWeights[newId] = {
      identity: newId,
      weight: audioHistory.weightHistory.totalNoteWeights[oldId].weight,
    };
  });

  return {
    ...audioHistory,
    weightHistory: {
      ...audioHistory.weightHistory,
      totalNoteWeights: newTotalNoteWeights,
    },
    chordsInScales: newChordsInScales,
    noteHistory: newNoteHistory,
    primaryScale: newPrimaryScale,
    primaryScaleId: newPrimaryScaleId,
  };
}
